import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EntityList, PageHead } from '../../components/EntityList';
import ContractEditor from './ContractEditor';
import { useLabels } from '../../hooks/useLabels';
import { useEditorReturn } from '../../hooks/useEditorReturn';
import { normalizeReturnTo } from '../../utils/editorReturn';

function AddContract() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { id } = useParams();
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const returnTo = searchParams.get('returnTo');
	const { onReturn, returnLabel } = useEditorReturn();

	// Precise per-route prefixes (see App.js) — not a substring test, so a
	// future path segment that happens to contain "account"/"asset" can't
	// accidentally match.
	const preloadAccountId = pathname.startsWith('/AdminScreen/accounts/account/') ? id : null;
	const preloadAssetId = pathname.startsWith('/AdminScreen/assets/asset/') ? id : null;

	// Contextual parent, else the list.
	const backTo = preloadAccountId
		? `/AdminScreen/accounts/account/${preloadAccountId}`
		: preloadAssetId
			? `/AdminScreen/assets/asset/${preloadAssetId}`
			: '/AdminScreen/contracts';

	const breadcrumb = preloadAccountId
		? [
			{ label: 'Inicio', to: '/AdminScreen' },
			{ label: t('entity.account.plural'), to: '/AdminScreen/accounts' },
			{ label: 'Cuenta', to: `/AdminScreen/accounts/account/${preloadAccountId}` },
			{ label: `Nuevo ${t('entity.contract')}` },
		]
		: preloadAssetId
			? [
				{ label: 'Inicio', to: '/AdminScreen' },
				{ label: t('entity.asset.plural'), to: '/AdminScreen/assets' },
				{ label: 'Activo', to: `/AdminScreen/assets/asset/${preloadAssetId}` },
				{ label: `Nuevo ${t('entity.contract')}` },
			]
			: [
				{ label: 'Inicio', to: '/AdminScreen' },
				{ label: t('entity.contract.plural'), to: '/AdminScreen/contracts' },
				{ label: `Nuevo ${t('entity.contract')}` },
			];

	// returnTo wins over the contextual parent so cancelling lands back on the
	// exact origin (e.g. an account's Alquileres tab), not the account's default tab.
	const handleCancel = () => navigate(normalizeReturnTo(returnTo) || backTo);
	const handleSaved = (savedContractId) => {
		if (returnTo) {
			// Resolved against our own origin so an absolute/protocol-relative
			// returnTo can't navigate off-site — only pathname+search are used.
			const target = new URL(returnTo, window.location.origin);
			target.searchParams.set('contractId', savedContractId);
			navigate(`${target.pathname}${target.search}`);
		} else if (preloadAccountId) {
			// Created from an account: return there instead of the contract detail.
			navigate(backTo);
		} else if (preloadAssetId) {
			navigate(`${backTo}/contracts`);
		} else {
			navigate(`/AdminScreen/contracts/contract/${savedContractId}`);
		}
	};

	return (
		<EntityList>
			<Helmet>
				<title>{`${t('action.add')} ${t('entity.contract')}`}</title>
			</Helmet>
			<PageHead
				breadcrumb={breadcrumb}
				title={`Nuevo ${t('entity.contract')}`}
				subtitle={`Completá los datos principales del ${t('entity.contract').toLowerCase()}.`}
			/>
			<ContractEditor
				mode='create'
				onCancel={handleCancel}
				onSaved={handleSaved}
				onReturn={onReturn}
				returnLabel={returnLabel}
				saveLabel='Agregar'
				preloadAccountId={preloadAccountId}
				preloadAssetId={preloadAssetId}
			/>
		</EntityList>
	);
}
export default AddContract;
